import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { IGetPatientParams, IPatient } from '~/interfaces'
import { ForbiddenError, HttpStatusCode, NotFoundError, UnexpectedError } from '~/protocols/http'
import { fetchPatient, message } from '~/services'
import { hasObjectValue } from '~/utils'

const UNIQUE_KEY = 'patient'

type ErrorType = Error | NotFoundError | UnexpectedError

export const useFetchPatient = (fetchParams: IGetPatientParams | null): UseQueryResult<IPatient, ErrorType> => {
  const hasFetchParams = hasObjectValue({ ...fetchParams })

  const fetcher = async () => {
    if (!fetchParams) throw new Error('fetchParams is required')

    try {
      const response = await fetchPatient(fetchParams)
      const patientHasNoCompleteData = !response?.id

      if (patientHasNoCompleteData) {
        throw `Prontuário ${fetchParams.pathParams.medicalRecord} contém informações incompletas.`
      }

      return response
    } catch (error: any) {
      if (typeof error === 'string') throw new Error(error)
      if (error?.status === HttpStatusCode.notFound) throw new NotFoundError(message('patient.not_found'))
      if (error?.status === HttpStatusCode.forbidden) throw new ForbiddenError(message('patient.forbidden'))

      throw new UnexpectedError(message('patient.unexpected_error'))
    }
  }

  return useQuery<IPatient, ErrorType>([UNIQUE_KEY, fetchParams], fetcher, {
    enabled: hasFetchParams,
    cacheTime: Infinity,
    staleTime: Infinity
  })
}
